import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import api from '../../services/api';
import { DEPARTMENTS } from '../../utils/constants';

const TeamAttendancePage = () => {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    employee_id: '',
    status: ''
  });

  const [page, setPage] = useState(1);
  const [taskModalRecord, setTaskModalRecord] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployees = async () => {
    try {
      // Fetch all employees without pagination limit
      const response = await api.get('/employees', { params: { limit: 1000 } });
      const employees = response.data.data?.employees || [];
      setAllEmployees(Array.isArray(employees) ? employees : []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      setAllEmployees([]);
    }
  };

  const fetchAttendance = async () => {
    if (!user) return; // Don't fetch if user is not loaded

    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };

      // Use /attendance/all endpoint for all team attendance views
      // Backend will handle authorization (admin, HR, manager role, or reporting managers)
      const endpoint = '/attendance/all';

      const response = await api.get(endpoint, { params });
      const data = response.data.data || {};
      const records = data.records || [];
      const paginationData = data.pagination || null;

      setAttendanceRecords(Array.isArray(records) ? records : []);
      setPagination(paginationData);
    } catch (error) {
      console.error('Failed to load attendance:', error);
      setAttendanceRecords([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      employee_id: '',
      status: ''
    });
    setPage(1);
  };

  const calculateHours = (checkIn, checkOut) => {
    if (!checkOut) return 'N/A';
    const hours = (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60);
    return `${hours.toFixed(2)}h`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPageTitle = () => {
    if (!user) return 'Team Attendance';
    if (user.role === 'admin') return 'All Employee Attendance';
    if (user.departments?.includes(DEPARTMENTS.HR)) return 'All Employee Attendance';
    return 'Team Attendance';
  };

  const getPageDescription = () => {
    if (!user) return 'Loading...';
    if (user.role === 'admin') return 'View attendance records for all employees';
    if (user.departments?.includes(DEPARTMENTS.HR)) return 'View attendance records for all employees';
    return 'View attendance records for your team members';
  };

  if (!user) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <div>
            <h1>{getPageTitle()}</h1>
            <p className="mb-0 text-muted">{getPageDescription()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <h3 className="fs-6 mb-3">Filters</h3>
            <div className="row g-3 mb-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">End Date</label>
                <input
                  type="date"
                  name="end_date"
                  className="form-control"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Employee</label>
                <select
                  name="employee_id"
                  className="form-select"
                  value={filters.employee_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All Employees</option>
                  {Array.isArray(allEmployees) && allEmployees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employee_code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="checked_in">Checked In</option>
                  <option value="checked_out">Checked Out</option>
                </select>
              </div>
            </div>

            <button className="btn btn-outline-secondary" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {/* Attendance Records */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Tasks Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No attendance records found
                      </td>
                    </tr>
                  ) : (
                    attendanceRecords.map((record) => (
                      <tr key={record._id} className="align-middle">
                        <td className="fw-medium">{formatDate(record.date)}</td>
                        <td>
                          <div className="fw-medium">{record.employee_id?.name}</div>
                          <div className="text-muted small">
                            {record.employee_id?.employee_code}
                          </div>
                        </td>
                        <td>{formatTime(record.check_in)}</td>
                        <td>{formatTime(record.check_out)}</td>
                        <td className="fw-medium text-primary">
                          {calculateHours(record.check_in, record.check_out)}
                        </td>
                        <td>
                          <span className={`badge ${record.status === 'checked_in' ? 'bg-success' : 'bg-primary'}`}>
                            {record.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {record.tasks_completed ? (
                            <div className="d-flex align-items-center gap-2">
                              <span className="small text-truncate" style={{ maxWidth: '220px' }}>
                                {record.tasks_completed}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary flex-shrink-0"
                                onClick={() => setTaskModalRecord(record)}
                              >
                                View
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {pagination && (
                <div className="p-3">
                  <Pagination pagination={pagination} onPageChange={setPage} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {taskModalRecord && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Tasks Completed</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setTaskModalRecord(null)}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-medium">{taskModalRecord.employee_id?.name}</div>
                    <div className="text-muted small">
                      {taskModalRecord.employee_id?.employee_code} &bull; {formatDate(taskModalRecord.date)}
                    </div>
                  </div>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {taskModalRecord.tasks_completed}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setTaskModalRecord(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default TeamAttendancePage;
